// src/features/scheduling/pages/SchedulingDemographics.tsx
import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "sonner";
import {
  Calendar,
  User,
  FlaskConical,
  MapPin,
  Check,
  ChevronRight,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { useSelector } from "react-redux";
import type { RootState } from "@/store";

// Components
import PersonSearch from "../components/PersonSearch";
import TestSelection from "../components/TestSelection";
import LocationSearch from "../components/LocationSearch";
import AddPersonDialog from "../components/AddPersonDialog";

// Types
import type {
  Visitor,
  TestSelection as TestSelectionType,
  VendorLocation,
  CreatePersonRequest,
  AppointmentEmailRecipient,
} from "../types";

// API hooks
import { useListTestTypesQuery } from "@/features/tests/services/testApi";
import {
  useListPersonsQuery,
  useCreatePersonMutation,
  useLazySearchVendorLocationsQuery,
  useScheduleAppointmentMutation,
  useLazyCheckExistingAppointmentQuery,
  useLazyGetClinicPricesQuery,
} from "../services/schedulingApi";
import { useGetClientsQuery } from "@/api/clientApi";
import { formatUsDate } from "@/utils/date";

// Steps configuration
const STEPS = [
  {
    id: 1,
    title: "Select Tests",
    icon: FlaskConical,
    description: "Choose required tests",
  },
  {
    id: 2,
    title: "Find Location",
    icon: MapPin,
    description: "Find nearby testing center",
  },
  {
    id: 3,
    title: "Select Visitor",
    icon: User,
    description: "Search or add a visitor",
  },
  {
    id: 4,
    title: "Visit Date",
    icon: Calendar,
    description: "Choose visit date/time",
  },
  { id: 5, title: "Review", icon: Check, description: "Review and confirm" },
];

// Helper to format distance for display - show 2 decimal places (1.30 Miles) using car/driving distance
function formatDistance(distance: number | undefined): string {
  if (!distance && distance !== 0) return "N/A";
  return `${distance.toFixed(2)} Miles`;
}


function isValidDateOnly(value?: string): boolean {
  if (!value) return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function formatVisitDate(value?: string): string {
  if (!value) return "Not selected";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "Not selected";
  return formatUsDate(date);
}

function parseTestComponents(raw?: string): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    let source: unknown[] = [];

    if (Array.isArray(parsed)) {
      source = parsed;
    } else if (parsed && typeof parsed === "object") {
      const maybeComponents = (parsed as { components?: unknown }).components;
      if (Array.isArray(maybeComponents)) {
        source = maybeComponents;
      }
    }

    return source
      .filter((component): component is string => typeof component === "string")
      .map((component) => component.trim())
      .filter(Boolean);
  } catch {
    // Backward compatibility for any non-JSON legacy value.
    return raw
      .split(",")
      .map((component) => component.trim())
      .filter(Boolean);
  }
}

export default function SchedulingDemographics() {
  // State
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPerson, setSelectedPerson] = useState<Visitor | null>(null);
  const [selectedTests, setSelectedTests] = useState<TestSelectionType[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<VendorLocation | null>(null);
  const [visitDateTime, setVisitDateTime] = useState("");
  const [emailRecipient, setEmailRecipient] = useState<AppointmentEmailRecipient>("BOTH");
  const [vendorLocations, setVendorLocations] = useState<VendorLocation[]>([]);
  const [lastLocationSearch, setLastLocationSearch] = useState<{ zipCode: string; radius: number } | null>(null);
  const [isAddPersonOpen, setIsAddPersonOpen] = useState(false);
  const [isSearchingLocations, setIsSearchingLocations] = useState(false);
  const [bookingFinished, setBookingFinished] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  // API calls
  const { user } = useSelector((state: RootState) => state.auth);
  const isClientRole = user?.role === "CLIENT" || user?.role === "CLIENT_EMPLOYEE";

  const { data: testsData = [], isLoading: isLoadingTests } = useListTestTypesQuery();
  const { data: clientsData = [] } = useGetClientsQuery(undefined, { skip: user?.role !== "SUPER_ADMIN" });
  
  const { data: personsData = [], isLoading: isLoadingPersons } = useListPersonsQuery(
    isClientRole ? { clientId: user?.clientId } : (selectedClientId ? { clientId: selectedClientId } : undefined),
    { skip: (isClientRole && !user?.clientId) }
  );
  const [createPerson, { isLoading: isCreatingPerson }] = useCreatePersonMutation();
  const [searchVendorLocations] = useLazySearchVendorLocationsQuery();
  const [getClinicPrices] = useLazyGetClinicPricesQuery();
  const [scheduleAppointment, { isLoading: isScheduling }] = useScheduleAppointmentMutation();
  const [checkExistingAppointment, { isFetching: isCheckingAppointment }] = useLazyCheckExistingAppointmentQuery();

  const [hasExistingAppointment, setHasExistingAppointment] = useState(false);

  const resetFromStep = useCallback((step: number) => {
    if (step <= 1) {
      setSelectedTests([]);
    }

    if (step <= 2) {
      setSelectedVendor(null);
      setVendorLocations([]);
      setLastLocationSearch(null);
      setSelectedClientId(null);
    }

    if (step <= 3) {
      setSelectedPerson(null);
    }

    if (step <= 4) {
      setVisitDateTime("");
      setHasExistingAppointment(false);
    }

    if (step <= 5) {
      setEmailRecipient("BOTH");
    }
  }, []);


  // Calculate final prices for selected tests by using backend-provided values directly.
  // The backend PricingResolutionService handles all margin and fee resolution.
  const calculatedPrices = useMemo(() => {
    if (!selectedVendor || !selectedTests.length) return {};

    const prices: Record<number, { basePrice: number; margin: number; collectionFee: number; finalPrice: number; testPriceWithMargin: number }> = {};

    selectedTests.forEach(test => {
      // Backend testPrices already includes the final price (Base + Margin + Fee)
      // provided by the PricingResolutionService.
      const vendorTestPrice = selectedVendor.testPrices?.[test.id.toString()];
      const finalPrice = Number(vendorTestPrice !== undefined ? vendorTestPrice : (selectedVendor.price || 0));

      prices[test.id] = {
        basePrice: finalPrice,
        margin: 0,
        collectionFee: 0,
        finalPrice,
        testPriceWithMargin: finalPrice
      };
    });

    return prices;
  }, [selectedVendor, selectedTests]);

  const totalAmount = useMemo(
    () => Object.values(calculatedPrices).reduce((sum, item) => sum + item.finalPrice, 0),
    [calculatedPrices]
  );

  const vendorTotalAmountByClinicId = useMemo<Record<number, number>>(
    () =>
      Object.fromEntries(
        vendorLocations.map((vendor) => [
          vendor.id,
          Number(vendor.price || 0),
        ])
      ),
    [vendorLocations]
  );


  // Transform tests for selection
  const testsForSelection = useMemo((): TestSelectionType[] => {
    return testsData
      .filter((test) => test.status === "ACTIVE")
      .map((test) => ({
        id: test.id,
        testName: test.testName,
        testCategory: test.testCategory,
        testDescription: test.testDescription,
        testComponents: parseTestComponents(test.testComponents),
        selected: false,
      }));
  }, [testsData]);


  // Handlers (step order: 1=Tests, 2=Location, 3=Visitor, 4=Visit Date, 5=Review)

  const handleSelectVendor = useCallback((vendor: VendorLocation | null) => {
    setSelectedVendor(vendor);
  }, []);

  const handleSelectTest = useCallback((test: TestSelectionType) => {
    setSelectedTests((prev) => [...prev, { ...test, selected: true }]);
  }, []);

  const handleDeselectTest = useCallback((testId: number) => {
    setSelectedTests((prev) => prev.filter((t) => t.id !== testId));
  }, []);

  const handleSelectAllTests = useCallback((tests: TestSelectionType[]) => {
    setSelectedTests(tests.map((t) => ({ ...t, selected: true })));
  }, []);

  const handleSelectPerson = useCallback((person: Visitor | null) => {
    setSelectedPerson(person);
  }, []);

  const handleLocationSearch = useCallback(
    async (zipCode: string, radius: number) => {
      setIsSearchingLocations(true);
      try {
        const uniqueTestIds = [...new Set(selectedTests.map((t) => t.id))];

        // Ensure we use the most relevant clientId: 
        // 1. If it's a CLIENT logged in, use their user.clientId
        // 2. If it's a Super Admin, use selectedClientId
        const effectiveClientId = isClientRole ? user?.clientId : selectedClientId;

        const result = await searchVendorLocations({
          zipCode,
          radius,
          testIds: uniqueTestIds.length > 0 ? uniqueTestIds : undefined,
          clientId: (effectiveClientId ?? undefined) as number | undefined,
        }).unwrap();
        const locations = Array.isArray(result) ? result : [];
        setVendorLocations(locations);
        setLastLocationSearch({ zipCode, radius });
        setSelectedVendor((prev) =>
          prev ? (locations.find((l) => l.id === prev.id) || null) : null
        );
      } catch {
        setVendorLocations([]);
        setSelectedVendor(null);
        toast.error("Failed to search locations");
      } finally {
        setIsSearchingLocations(false);
      }
    },
    [searchVendorLocations, selectedTests, selectedPerson?.clientId, user?.clientId, isClientRole, selectedClientId]
  );

  // When a person is selected (especially by Super Admin),
  // refresh the location search (or targeted clinic prices) to apply the correct client margins.
  useEffect(() => {
    const refreshPricing = async () => {
      const clientId = isClientRole ? user?.clientId : selectedClientId;
      if (!clientId || !lastLocationSearch) return;

      // OPTIMIZATION: If we already have a selected clinic, only refresh its prices.
      if (selectedVendor) {
        try {
          const uniqueTestIds = [...new Set(selectedTests.map((t) => t.id))];
          const updatedPrices = await getClinicPrices({
            clinicId: selectedVendor.id,
            testIds: uniqueTestIds,
            clientId: clientId,
          }).unwrap();

          // Calculate new total price for the selected clinic
          const newTotalPrice = Object.values(updatedPrices).reduce((sum, p) => sum + Number(p), 0);

          setSelectedVendor((prev) =>
            prev
              ? {
                ...prev,
                testPrices: updatedPrices,
                price: newTotalPrice,
              }
              : null
          );
        } catch (err) {
          console.error("Failed to refresh targeted clinic prices, falling back to full search", err);
          handleLocationSearch(lastLocationSearch.zipCode, lastLocationSearch.radius);
        }
      } else {
        // No selected clinic yet, re-run full search to show correct pricing in cards
        handleLocationSearch(lastLocationSearch.zipCode, lastLocationSearch.radius);
      }
    };

    refreshPricing();
  }, [
    selectedPerson?.id,
    selectedPerson?.clientId,
    lastLocationSearch?.zipCode,
    lastLocationSearch?.radius,
    handleLocationSearch,
    selectedVendor?.id, // Only trigger if vendor identity changes, not all vendor props
    getClinicPrices,
  ]);

  // When returning to Step 1 (Select Tests), clear the workflow state 
  // if a previous booking was just finished.
  useEffect(() => {
    if (currentStep === 1 && bookingFinished) {
      setSelectedPerson(null);
      setSelectedClientId(null);
      setSelectedTests([]);
      setSelectedVendor(null);
      setVisitDateTime("");
      setVendorLocations([]);
      setBookingFinished(false); // Reset the flag
    }
  }, [currentStep, bookingFinished]);


  const handleAddPerson = useCallback(
    async (person: CreatePersonRequest) => {
      try {
        const newPerson = await createPerson(person).unwrap();
        setSelectedPerson(newPerson as Visitor);
        setIsAddPersonOpen(false);
      } catch (err: unknown) {
        const message = err && typeof err === "object" && "data" in err
          ? (err as { data?: { error?: string } }).data?.error
          : "Failed to add visitor";
        toast.error(String(message));
        throw err;
      }
    },
    [createPerson]
  );

  const handleProceed = useCallback(async () => {
    if (currentStep === 4) {
      if (!isValidDateOnly(visitDateTime)) {
        toast.error("Please select a valid visit date");
        return;
      }

      const selectedDate = new Date(`${visitDateTime}T00:00:00`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        toast.error("Visit date cannot be in the past");
        return;
      }

      if (selectedPerson) {
        try {
          const result = await checkExistingAppointment({
            visitorId: selectedPerson.id,
            date: visitDateTime
          }).unwrap();
          setHasExistingAppointment(result.hasAppointment);
        } catch (err) {
          console.error("Failed to check existing appointments", err);
          setHasExistingAppointment(false);
        }
      }
    }

    if (currentStep < STEPS.length) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, selectedPerson, visitDateTime, checkExistingAppointment]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      const previousStep = currentStep - 1;
      resetFromStep(previousStep);
      setCurrentStep(previousStep);
    }
  }, [currentStep, resetFromStep]);

  const handleSubmit = useCallback(async () => {
    if (selectedTests.length === 0) {
      toast.error("Please select at least one test");
      return;
    }
    if (!selectedVendor) {
      toast.error("Please select a testing location");
      return;
    }
    if (!selectedPerson) {
      toast.error("Please select or add a visitor");
      return;
    }
    if (!selectedPerson.clientId || !selectedPerson.dateOfBirth || !selectedPerson.gender) {
      toast.error("Visitor must have client, date of birth, and gender before scheduling");
      return;
    }
    if (!isValidDateOnly(visitDateTime)) {
      toast.error("Please select a valid visit date");
      return;
    }

    const selectedVisitDate = new Date(`${visitDateTime}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedVisitDate < today) {
      toast.error("Visit date cannot be in the past");
      return;
    }

    try {
      const response = await scheduleAppointment({
        visitorId: selectedPerson.id,
        clinicId: selectedVendor.id,
        testTypeIds: [...new Set(selectedTests.map((t) => t.id))],
        scheduledAt: `${visitDateTime}T00:00:00`,
        emailRecipient,
      }).unwrap();
      toast.success(
        response?.orderNumber
          ? `Appointment scheduled successfully! Order #${response.orderNumber}`
          : "Appointment scheduled successfully!"
      );
      setVisitDateTime("");
      setEmailRecipient("BOTH");
      setVendorLocations([]);
      setBookingFinished(true); // Flag that we finished
      setCurrentStep(1);
    } catch (err: unknown) {
      const message = err && typeof err === "object" && "data" in err
        ? (err as { data?: { error?: string } }).data?.error
        : "Failed to schedule appointment";
      toast.error(String(message));
    }
  }, [selectedPerson, selectedTests, selectedVendor, visitDateTime, emailRecipient, scheduleAppointment]);

  // Check if step is complete (step order: 1=Tests, 2=Location, 3=Visitor, 4=Visit Date, 5=Review)
  const isStepComplete = (step: number) => {
    switch (step) {
      case 1:
        return selectedTests.length > 0;
      case 2:
        return !!selectedVendor;
      case 3:
        return !!selectedPerson;
      case 4:
        return isValidDateOnly(visitDateTime);
      case 5:
        return !!selectedPerson && selectedTests.length > 0 && !!selectedVendor && isValidDateOnly(visitDateTime);
      default:
        return false;
    }
  };

  // Check if can proceed
  const canProceed = isStepComplete(currentStep);
  const activeStepMeta = STEPS.find((step) => step.id === currentStep);
  const progressPercent = Math.round((currentStep / STEPS.length) * 100);

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#c7d2fe_0%,_#eff6ff_28%,_#e2e8f0_100%)] dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-2">
        <div className="rounded-3xl border border-indigo-100 bg-white/85 p-6 shadow-xl backdrop-blur dark:border-slate-700 dark:bg-slate-900">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-700">Workflow Studio</p>
              <h1 className="mt-1 text-3xl font-black text-slate-900 dark:text-slate-100">
                Scheduling & Demographics
              </h1>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                Guided booking in 5 clear steps with real-time summary and pricing.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-2 text-right shadow-sm dark:border-slate-700 dark:bg-slate-800">
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Current Step</p>
                <p className="text-lg font-bold text-indigo-900 dark:text-slate-100">
                  {currentStep} / {STEPS.length}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedPerson(null);
                  setSelectedClientId(null);
                  setSelectedTests([]);
                  setSelectedVendor(null);
                  setVisitDateTime("");
                  setEmailRecipient("BOTH");
                  setVendorLocations([]);
                  setCurrentStep(1);
                }}
                className="gap-2 border-slate-300"
              >
                <RefreshCw className="w-4 h-4" />
                Start Over
              </Button>
            </div>
          </div>

          <div className="mt-4">
            <div className="h-2 w-full rounded-full bg-slate-200/80 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-slate-500">{progressPercent}% completed</p>
          </div>

          <div className="mt-5 overflow-x-auto">
            <div className="flex min-w-max items-center gap-2">
              {STEPS.map((step) => {
                const stepDone = isStepComplete(step.id);
                const stepActive = currentStep === step.id;
                return (
                  <Button
                    key={step.id}
                    variant="ghost"
                    onClick={() => {
                      if (step.id < currentStep) {
                        resetFromStep(step.id);
                        setCurrentStep(step.id);
                      }
                    }}
                    disabled={step.id >= currentStep}
                    className={`h-auto flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 border-2 ${stepActive
                      ? "border-indigo-600 bg-gradient-to-br from-indigo-600 to-blue-700 text-white shadow-lg scale-105"
                      : stepDone
                        ? "border-teal-200 bg-teal-50/50 text-teal-800 shadow-sm hover:bg-teal-100 hover:border-teal-300"
                        : "border-slate-100 bg-white/50 text-slate-400 opacity-60 hover:opacity-100"
                      }`}
                  >
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-black shadow-inner transition-colors ${stepActive
                        ? "bg-white/20 text-white"
                        : stepDone
                          ? "bg-teal-100 text-teal-600"
                          : "bg-slate-100 text-slate-400"
                        }`}
                    >
                      {stepDone && !stepActive ? <Check className="h-5 w-5 stroke-[3]" /> : step.id}
                    </div>
                    <div className="text-left hidden md:block">
                      <p className={`text-sm font-bold tracking-tight ${stepActive ? "text-white" : "text-slate-900"}`}>
                        {step.title}
                      </p>
                      <p className={`text-[10px] font-medium uppercase tracking-wider opacity-60 ${stepActive ? "text-indigo-100" : "text-slate-500"}`}>
                        {step.description}
                      </p>
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
          {/* Main Panel */}
          <div className="space-y-6 xl:col-span-8">
            <Card className="border-slate-200 bg-white/95 shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <CardContent className="p-4 md:p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Step {currentStep}</p>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                      {activeStepMeta?.title}
                    </h2>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{activeStepMeta?.description}</p>
                  </div>
                  <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-200">
                    Workflow Active
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {currentStep === 1 && (
              <TestSelection
                tests={testsForSelection}
                isLoading={isLoadingTests}
                selectedTests={selectedTests}
                onSelectTest={handleSelectTest}
                onDeselectTest={handleDeselectTest}
                onSelectAll={handleSelectAllTests}
                personHasTests={false}
              />
            )}

            {currentStep === 2 && (
              <LocationSearch
                vendors={vendorLocations}
                isLoading={isSearchingLocations}
                selectedVendor={selectedVendor}
                onSelectVendor={handleSelectVendor}
                onSearch={handleLocationSearch}
                defaultZipCode={selectedPerson?.zipCode || ""}
                vendorTotalsByClinicId={vendorTotalAmountByClinicId}
                isSuperAdmin={user?.role === "SUPER_ADMIN"}
                clients={clientsData}
                selectedClientId={selectedClientId}
                onSelectClient={setSelectedClientId}
              />
            )}

            {currentStep === 3 && (
              <PersonSearch
                persons={Array.isArray(personsData) ? personsData : []}
                isLoading={isLoadingPersons}
                selectedPerson={selectedPerson}
                onSelectPerson={handleSelectPerson}
                onAddPerson={() => setIsAddPersonOpen(true)}
              />
            )}

            {currentStep === 4 && (
              <Card className="border-2 border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-4 text-white">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                      <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">Select Visit Date</h3>
                      <p className="text-sm text-cyan-100">
                        Ask visitor when they can visit the clinic
                      </p>
                    </div>
                  </div>
                </div>

                <CardContent className="p-6">
                  <div className="space-y-2 max-w-md">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Visit Date
                    </label>
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                      This is the date we start coordinating with the clinic/lab. It is not the appointment date.
                    </p>
                    <DatePicker
                      value={visitDateTime}
                      onChange={setVisitDateTime}
                      minDate={new Date()}
                      className="w-full h-11"
                    />
                    <p className="text-xs text-slate-500">
                      Selected: {formatVisitDate(visitDateTime)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {currentStep === 5 && (
              <Card className="border-2 border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 text-white">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                      <Check className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">
                        Review & Confirm
                      </h3>
                      <p className="text-sm text-amber-100">
                        Please review all details before scheduling
                      </p>
                    </div>
                  </div>
                </div>

                <CardContent className="p-6 space-y-6">
                  {/* Visitor Summary */}
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-2 mb-3">
                      <User className="w-5 h-5 text-blue-600" />
                      <h4 className="font-semibold text-blue-800 dark:text-blue-200">
                        Visitor
                      </h4>
                    </div>
                    {selectedPerson && (
                      <div className="space-y-1 text-sm text-blue-700 dark:text-blue-300">
                        <p className="font-medium text-base">
                          {selectedPerson.firstName} {selectedPerson.lastName}
                        </p>
                        <p>
                          <span className="font-medium">Client / Employer:</span>{" "}
                          {selectedPerson.clientName || "-"}
                        </p>
                        <p>{selectedPerson.email}</p>
                        <p>{selectedPerson.phone}</p>
                      </div>
                    )}
                  </div>

                  {/* Tests Summary with Pricing */}
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                    <div className="flex items-center gap-2 mb-3">
                      <FlaskConical className="w-5 h-5 text-emerald-600" />
                      <h4 className="font-semibold text-emerald-800 dark:text-emerald-200">
                        Tests ({selectedTests.length})
                      </h4>
                    </div>
                    <div className="space-y-3">
                      {selectedTests.map((test) => {
                        const priceInfo = calculatedPrices[test.id];
                        return (
                          <div key={test.id} className="flex justify-between items-start text-sm p-2 bg-white/50 rounded">
                            <span className="font-medium text-emerald-900">{test.testName}</span>
                            <span className="font-mono text-emerald-700">
                              ${priceInfo ? priceInfo.finalPrice.toFixed(2) : '0.00'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Location Summary */}
                  <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-800">
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="w-5 h-5 text-violet-600" />
                      <h4 className="font-semibold text-violet-800 dark:text-violet-200">
                        Testing Location
                      </h4>
                    </div>
                    {selectedVendor && (
                      <div className="space-y-1 text-sm text-violet-700 dark:text-violet-300">
                        <p className="font-medium text-base">
                          {selectedVendor.clinicName}
                        </p>
                        <p>{selectedVendor.vendorName}</p>
                        <p>
                          {[selectedVendor.address, selectedVendor.city, selectedVendor.state, selectedVendor.zipCode]
                            .filter(Boolean)
                            .join(", ") || "Address not specified"}
                        </p>
                        <div className="flex items-center gap-4 mt-2">
                          <Badge
                            variant="default"
                            className="text-sm font-bold px-3 py-1 bg-blue-600"
                          >
                            <span className="font-bold">{formatDistance(selectedVendor.distance)}</span>
                          </Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <Badge
                            className={
                              selectedVendor.appointmentRequired
                                ? "bg-amber-100 text-amber-800 border-amber-200"
                                : "bg-green-100 text-green-800 border-green-200"
                            }
                          >
                            {selectedVendor.appointmentRequired ? "Appointment Required" : "Walk-ins Welcome"}
                          </Badge>
                          <Badge
                            className={
                              selectedVendor.cocRequired
                                ? "bg-rose-100 text-rose-800 border-rose-200"
                                : "bg-slate-100 text-slate-700 border-slate-200"
                            }
                          >
                            {selectedVendor.cocRequired ? "COC Required" : "COC Not Required"}
                          </Badge>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Visit Date Summary */}
                  <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar className="w-5 h-5 text-cyan-600" />
                      <h4 className="font-semibold text-cyan-800 dark:text-cyan-200">
                        Visit Date
                      </h4>
                    </div>
                    <p className="text-sm text-cyan-700 dark:text-cyan-300 font-medium">
                      {formatVisitDate(visitDateTime)}
                    </p>
                  </div>



                  {/* Warning */}
                  <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-700 dark:text-amber-300">
                      <p className="font-medium">Important</p>
                      <p>
                        Please ensure all information is correct before
                        scheduling. The selected recipient(s) will receive a
                        confirmation email with appointment details and form attachments.
                      </p>
                      {hasExistingAppointment && (
                        <div className="mt-3 p-3 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded border border-red-200 dark:border-red-800 font-medium flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 shrink-0" />
                          <span>Visitor has already booked an appointment for {formatVisitDate(visitDateTime)}. Booking again will create a new unique order.</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Navigation Buttons */}
            <Card className="border-slate-200 bg-white/95 shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <CardContent className="flex items-center justify-between p-4">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  className="gap-2 border-slate-300"
                >
                  Back
                </Button>

                {currentStep < STEPS.length ? (
                  <Button
                    onClick={handleProceed}
                    disabled={!canProceed || isCheckingAppointment}
                    className="gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 hover:from-blue-700 hover:to-indigo-700"
                  >
                    {isCheckingAppointment ? "Checking..." : "Continue"}
                    {!isCheckingAppointment && <ChevronRight className="w-4 h-4" />}
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={!canProceed || isScheduling}
                    className="gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 px-6 hover:from-emerald-700 hover:to-teal-700"
                  >
                    {isScheduling ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Scheduling...
                      </>
                    ) : (
                      <>
                        <Calendar className="w-4 h-4" />
                        Schedule Appointment
                      </>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Side Panel - Summary */}
          <div className="xl:col-span-4">
            <Card className="sticky top-6 overflow-hidden border border-slate-200 shadow-lg dark:border-slate-700">
              <div className="bg-gradient-to-r from-indigo-900 via-slate-900 to-cyan-900 p-4 text-white">
                <h3 className="font-semibold">Appointment Summary</h3>
              </div>
              <CardContent className="p-4 space-y-4">
                {/* Tests */}
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedTests.length > 0
                      ? "bg-green-100 text-green-600"
                      : "bg-slate-100 text-slate-400"
                      }`}
                  >
                    <FlaskConical className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-500">Tests</p>
                    {selectedTests.length > 0 ? (
                      <>
                        <p className="font-medium text-slate-800 dark:text-slate-200 truncate">
                          {selectedTests.length} test
                          {selectedTests.length !== 1 ? "s" : ""} selected
                        </p>
                        <div className="mt-2 space-y-1">
                          {selectedTests.map((test) => {
                            const priceInfo = calculatedPrices[test.id];
                            const showPrice =
                              !!selectedVendor &&
                              !!priceInfo &&
                              Number.isFinite(priceInfo.finalPrice);

                            return (
                              <div
                                key={`summary-test-${test.id}`}
                                className="flex justify-between text-xs text-slate-500 dark:text-slate-400"
                              >
                                <span className="truncate pr-2">{test.testName}</span>
                                <span className="tabular-nums">
                                  {showPrice ? `$${priceInfo.finalPrice.toFixed(2)}` : "-"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <p className="text-slate-400 italic">Not selected</p>
                    )}
                  </div>
                  {selectedTests.length > 0 && (
                    <Check className="w-5 h-5 text-green-500" />
                  )}
                </div>

                {/* Location */}
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedVendor
                      ? "bg-green-100 text-green-600"
                      : "bg-slate-100 text-slate-400"
                      }`}
                  >
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-500">Location</p>
                    {selectedVendor ? (
                      <p className="font-medium text-slate-800 dark:text-slate-200 truncate">
                        {selectedVendor.clinicName}
                      </p>
                    ) : (
                      <p className="text-slate-400 italic">Not selected</p>
                    )}
                  </div>
                  {selectedVendor && (
                    <Check className="w-5 h-5 text-green-500" />
                  )}
                </div>
                {/* Visitor */}
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedPerson
                      ? "bg-green-100 text-green-600"
                      : "bg-slate-100 text-slate-400"
                      }`}
                  >
                    <User className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-500">Visitor</p>
                    {selectedPerson ? (
                      <div>
                        <p className="font-medium text-slate-800 dark:text-slate-200 truncate">
                          {selectedPerson.firstName} {selectedPerson.lastName}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          Client / Employer: {selectedPerson.clientName || "-"}
                        </p>
                      </div>
                    ) : (
                      <p className="text-slate-400 italic">Not selected</p>
                    )}
                  </div>
                  {selectedPerson && (
                    <Check className="w-5 h-5 text-green-500" />
                  )}
                </div>

                {/* Visit Date */}
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${visitDateTime
                      ? "bg-green-100 text-green-600"
                      : "bg-slate-100 text-slate-400"
                      }`}
                  >
                    <Calendar className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-500">Visit Date</p>
                    {visitDateTime ? (
                      <p className="font-medium text-slate-800 dark:text-slate-200 truncate">
                        {formatVisitDate(visitDateTime)}
                      </p>
                    ) : (
                      <p className="text-slate-400 italic">Not selected</p>
                    )}
                  </div>
                  {visitDateTime && (
                    <Check className="w-5 h-5 text-green-500" />
                  )}
                </div>

                {/* Price Summary */}
                {selectedVendor && selectedTests.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex justify-between font-semibold text-slate-800 dark:text-slate-200 pt-2 border-t border-dashed border-slate-200 dark:border-slate-700">
                      <span>Total Amount</span>
                      <span>
                        $
                        {totalAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Add Visitor Dialog */}
      <AddPersonDialog
        open={isAddPersonOpen}
        onOpenChange={setIsAddPersonOpen}
        onSubmit={handleAddPerson}
        isSubmitting={isCreatingPerson}
      />
    </div>
  );
}
